import {
  Stack,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Autocomplete,
  FormHelperText
} from '@mui/material';
import { Fragment, useEffect, useState, useRef } from 'react';
import { styled } from '@mui/material/styles';
import Select from '@mui/material/Select';
import { FormikProps } from 'formik';
import { JoinFormType, PackagePrices } from 'types/join';
import 'intl-tel-input/build/css/intlTelInput.css';
import intlTelInput, { Iti, SomeOptions } from 'intl-tel-input';
import { useDispatch, useSelector } from 'react-redux';
import { states, getStateArrayByCountry, countries } from 'constants/locations';
import { backgrounds } from 'polished';
import { errorList, errorListTypes } from 'constants/error-list';
import { CountryCodeMap, CountryCodeArray } from './country-code';
import { PlanState } from '../reducers/plan';
import { EmailErrorsTextsExists } from './email-error-text';
import { resetError } from '../actions';

const ErrorIcon = styled('div')(({ theme }) => ({
  width: '16px',
  height: '16px',
  backgroundColor: (theme.palette as any).red,
  color: 'white',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  fontWeight: 'bold',
  position: 'absolute',
  top: '-8px',
  right: '-8px',
  zIndex: 1
}));

const SelectWrapper = styled('div')({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  width: '100%' // Ensure it matches the select width
});

const MainContainer = styled(Stack)(({ theme }) => ({
  width: '100%',
  paddingTop: '8px',
  paddingBottom: '20px',
  backgroundColor: 'transparent',
  '&:hover': {
    cursor: 'default'
  },
  '@media (max-width: 999px)': {
    width: '100%',
    padding: '8px 0',
    maxWidth: '500px',
    margin: '0 auto'
  }
}));
const TField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    border: `1px solid ${(theme.palette as any).siteCardBorder || '#E6E6EA'}`,
    backgroundColor: 'white',
    borderRadius: '8px',
    fontFamily: "'Inter', 'Montserrat-Regular', sans-serif",
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
  },
  '& .MuiInputBase-root.Mui-focused': {
    borderColor: (theme.palette as any).siteIndigo || '#5246E5',
    boxShadow: `0 0 0 3px ${(theme.palette as any).siteIndigoLight || '#EEEDFD'}`
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  },
  '& .MuiFormHelperText-root': {
    fontSize: '11px',
    marginLeft: '2px'
  },
  '& input.MuiInputBase-input': {
    border: 'none',
    color: (theme.palette as any).siteBlack,
    fontFamily: "'Inter', 'Montserrat-Regular', sans-serif",
    fontSize: '14px',
    padding: '10px 14px'
  },
  '& input.MuiInputBase-input::placeholder': {
    color: '#9D9DA6',
    opacity: 1,
    fontFamily: "'Inter', 'Montserrat-Regular', sans-serif",
    fontSize: '14px'
  },
  '& .Mui-disabled': {
    backgroundColor: (theme.palette as any).siteCardBackground || '#F4F4F6',
    '&:hover': {
      cursor: 'default'
    }
  },
  '& .iti__flag, & .iti__selected-flag, & .iti__selected-dial-code, & .iti__country-list, & .iti__selected-country':
    {
      cursor: 'default',
      '&:hover': {
        cursor: 'default'
      }
    },
  '@media (max-width: 999px)': {
    '& input.MuiInputBase-input': {
      fontSize: '14px'
    }
  }
}));
const TSelect = styled(Select)(({ theme }) => ({
  backgroundColor: 'white',
  border: `1px solid ${(theme.palette as any).siteCardBorder || '#E6E6EA'}`,
  borderRadius: '8px',
  fontFamily: "'Inter', 'Montserrat-Regular', sans-serif",
  fontSize: '14px',
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  },
  '&.Mui-focused': {
    borderColor: (theme.palette as any).siteIndigo || '#5246E5',
    boxShadow: `0 0 0 3px ${(theme.palette as any).siteIndigoLight || '#EEEDFD'}`
  },
  '& .MuiSvgIcon-root': {
    color: (theme.palette as any).siteBlack,
    opacity: 0.55,
    '&:hover': {
      cursor: 'default'
    }
  },
  '& .Mui-disabled': {
    backgroundColor: (theme.palette as any).siteCardBackground || '#F4F4F6',
    '&:hover': {
      cursor: 'default'
    }
  },
  '@media (max-width: 999px)': {
    fontSize: '12px'
  }
}));
const Fieldset = styled('fieldset')(({ disabled }) => ({
  border: 'none',
  padding: 0,
  opacity: disabled ? 0.5 : 1
}));

const StyledAutocomplete = styled(Autocomplete)(({ theme, disabled }) => ({
  backgroundColor: disabled
    ? (theme.palette as any).siteCardBackground || '#F4F4F6'
    : (theme.palette as any).white,
  borderRadius: '8px',
  '& .MuiIconButton-root': {
    color: (theme.palette as any).siteBlack,
    opacity: 0.55,
    '&:hover': {
      cursor: 'default'
    }
  },
  '& .MuiAutocomplete-inputRoot, & .MuiAutocomplete-inputRoot.MuiInputBase-sizeSmall': {
    padding: '0 39px 0 0'
  },
  '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input, & .MuiAutocomplete-inputRoot.MuiInputBase-sizeSmall .MuiAutocomplete-input':
    {
      padding: '10px 14px'
    },
  '& .MuiAutocomplete-endAdornment': {
    top: 'calc(50% - 14px)'
  }
}));

const FieldLabel = ({
  text,
  required,
  error,
  children
}: {
  text: string;
  required?: boolean;
  children: React.ReactNode;
  error?: boolean;
}) => {
  return (
    <Stack>
      <Stack direction="row" gap={0.25} sx={{ marginBottom: '6px' }}>
        <Typography
          variant="body1"
          sx={{
            fontFamily: 'Inter, Montserrat-SemiBold, sans-serif',
            fontWeight: 500,
            fontSize: '14px',
            color: '#434343',
            letterSpacing: '0.1px',
            lineHeight: 1.2
          }}
        >
          {text}
        </Typography>
        {required && (
          <Typography variant="body1" sx={{ color: '#C10230', fontSize: '14px', lineHeight: 1.2 }}>
            *
          </Typography>
        )}
        {error && <ErrorIcon>!</ErrorIcon>}
      </Stack>
      {children}
    </Stack>
  );
};

export const Details = ({
  form,
  disabled = false,
  isPriceList = false,
  selectedPlan
}: {
  form: FormikProps<JoinFormType>;
  disabled: boolean;
  isPriceList: boolean;
  selectedPlan: PackagePrices | undefined;
}) => {
  const [mobile, setMobile] = useState<Iti>();
  const [office, setOffice] = useState<Iti>();
  const prevCountryRef = useRef<string | null>(null);
  const didMountRef = useRef(false);
  const { companyTypes, showErrorPopup, subscriptionId } =
    useSelector((state: { plan: PlanState }) => state.plan) || {};
  const errorMessageType = errorList[showErrorPopup.errorMessage || ''] || '';
  const dispatch = useDispatch();

  useEffect(() => {
    if (
      errorMessageType === errorListTypes.emailAlreadyExists ||
      errorMessageType === errorListTypes.emailIsNotValid
    ) {
      dispatch(resetError());
    }
  }, [form.values.email]);

  useEffect(() => {
    if (
      Object.keys(form.errors).length > 0 ||
      (showErrorPopup.show &&
        (errorMessageType === errorListTypes.emailAlreadyExists ||
          errorMessageType === errorListTypes.emailIsNotValid))
    ) {
      const formContainer = document.getElementById('details-section-id');
      if (formContainer) {
        const elementPosition = formContainer.getBoundingClientRect().top + window.scrollY;
        const offset = 50;
        window.scrollTo({
          top: elementPosition - offset,
          behavior: 'smooth'
        });
      }
    }
  }, [showErrorPopup, form.isSubmitting]);

  useEffect(() => {
    if (getStateArrayByCountry(form.values.companyCountry ?? '').length === 0) {
      form.setFieldValue('accountState', '');
    }
  }, [form.values.companyCountry]);

  useEffect(() => {
    // Stub the intlTelInputUtils functions to prevent errors when utils script doesn't load
    if (typeof window !== 'undefined') {
      if (!(window as any).intlTelInputUtils) {
        (window as any).intlTelInputUtils = {};
      }
      if (!(window as any).intlTelInputUtils.numberFormat) {
        (window as any).intlTelInputUtils.numberFormat = {
          E164: 0,
          INTERNATIONAL: 1,
          NATIONAL: 2,
          RFC3966: 3
        };
      }
      if (!(window as any).intlTelInputUtils.formatNumberAsYouType) {
        (window as any).intlTelInputUtils.formatNumberAsYouType = (number: string) => {
          return number; // Return as-is, no formatting
        };
      }
      if (!(window as any).intlTelInputUtils.formatNumber) {
        (window as any).intlTelInputUtils.formatNumber = (
          number: string,
          countryCode: string,
          format: number
        ) => {
          // Return E.164 format (format 0) - just return the number with + if it doesn't have it
          if (number && !number.startsWith('+')) {
            return `+${number}`;
          }
          return number || '';
        };
      }
    }

    const officeInput = document.querySelector('#office-number') as HTMLInputElement;
    const mobileInput = document.querySelector('#mobile-number') as HTMLInputElement;
    const currentCountry = form.values.companyCountry || '';
    // Only treat this as a country change when there was a previous, non-empty
    // country. Going from no country (initial load, geolocation, or cookie/cache
    // prefill) to a country must NOT clear a prefilled phone/mobile number.
    const countryChanged =
      didMountRef.current &&
      Boolean(prevCountryRef.current) &&
      prevCountryRef.current !== currentCountry;
    prevCountryRef.current = currentCountry;
    if (
      form.values.companyCountry &&
      form.values.companyCountry !== null &&
      form.values.companyCountry !== ''
    ) {
      if (countryChanged) {
        if (officeInput) officeInput.value = '';
        if (mobileInput) mobileInput.value = '';
        if (form.values.phone) form.setFieldValue('phone', '');
        if (form.values.cellPhone) form.setFieldValue('cellPhone', '');
      }
      const countryCode =
        CountryCodeArray.find(
          (country: { country: string; countryCode: string; phonePrefix: string }) =>
            country.country === form.values.companyCountry
        )?.countryCode ||
        CountryCodeMap[form.values.companyCountry as keyof typeof CountryCodeMap]?.countryCode ||
        'auto';
      if (officeInput) {
        if (office) {
          office.destroy();
        }
        const phone = intlTelInput(
          officeInput as HTMLInputElement,
          {
            initialCountry: countryCode,
            separateDialCode: true,
            utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js',
            allowDropdown: false,
            showSelectedDialCode: true,
            showFlags: true,
            onlyCountries: [countryCode],
            formatAsYouType: false
          } as SomeOptions
        );

        setOffice(phone);

        // Sync phone value on blur to ensure validation runs
        officeInput.addEventListener('blur', () => {
          try {
            const fullNumber = phone.getNumber();
            if (fullNumber) {
              form.setFieldValue('phone', fullNumber);
            } else {
              // If no valid number, use the input value
              const inputValue = officeInput.value.replace(/\D/g, '');
              if (inputValue) {
                const dial = phone.getSelectedCountryData()?.dialCode;
                const e164 = dial ? `+${dial}${inputValue}` : inputValue;
                form.setFieldValue('phone', e164);
              } else {
                form.setFieldValue('phone', '');
              }
            }
          } catch (error) {
            // If getNumber fails, construct manually
            const inputValue = officeInput.value.replace(/\D/g, '');
            if (inputValue) {
              const dial = phone.getSelectedCountryData()?.dialCode;
              const e164 = dial ? `+${dial}${inputValue}` : inputValue;
              form.setFieldValue('phone', e164);
            } else {
              form.setFieldValue('phone', '');
            }
          }
          form.setFieldTouched('phone', true);
        });

        const selectedDialCodeElement =
          (officeInput && officeInput.closest('.iti')?.querySelector('.iti__selected-dial-code')) ||
          (mobileInput && mobileInput.closest('.iti')?.querySelector('.iti__selected-dial-code'));

        if (selectedDialCodeElement) {
          selectedDialCodeElement.classList.add('no-cursor-pointer');
        }
      }
      if (mobileInput) {
        if (mobile) {
          mobile.destroy();
        }
        const phone = intlTelInput(
          mobileInput as HTMLInputElement,
          {
            initialCountry: countryCode,
            separateDialCode: true,
            utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js',
            allowDropdown: false,
            showSelectedDialCode: true,
            showFlags: true,
            onlyCountries: [countryCode],
            formatAsYouType: false
          } as SomeOptions
        );
        setMobile(phone);
      }
    }
    if (!didMountRef.current) {
      didMountRef.current = true;
    }
  }, [form.values.companyCountry, isPriceList]);

  const changeOfficePhone = (val: string) => {
    if (!office) return;

    // Try to get the full E.164 number from intl-tel-input
    try {
      const fullNumber = office.getNumber();
      if (fullNumber) {
        form.setFieldValue('phone', fullNumber);
        return;
      }
    } catch (error) {
      // If getNumber fails (utils not loaded), fall through to manual construction
    }

    // Fallback: construct the number manually
    const dial = office.getSelectedCountryData()?.dialCode;
    const digits = (val || '').replace(/\D/g, '');
    const e164 = dial ? `+${dial}${digits}` : digits;
    form.setFieldValue('phone', e164);
  };
  const changeMobilePhone = (val: string) => {
    const dial = mobile?.getSelectedCountryData()?.dialCode;
    const digits = (val || '').replace(/\D/g, '');
    const e164 = dial ? `+${dial}${digits}` : digits;
    form.setFieldValue('cellPhone', e164);
  };
  const getPhoneValue = (isMobile = false) => {
    const phone = isMobile ? form.values.cellPhone : form.values.phone;
    const countryCode = isMobile
      ? mobile?.getSelectedCountryData()?.dialCode
      : office?.getSelectedCountryData()?.dialCode;

    if (countryCode && phone && phone.startsWith(`+${countryCode}`)) {
      const local = phone.slice(countryCode.length + 1);
      return local.replace(/\D/g, '');
    }

    return phone ? phone.replace(/\D/g, '') : phone;
  };

  const hasStateOptions = getStateArrayByCountry(form.values.companyCountry ?? '').length > 0;
  const stateFields = hasStateOptions ? ['state'] : [];

  const rapnetFields = [
    'firstName',
    'lastName',
    'email',
    'companyName',
    'companyType',
    'companyCountry',
    'address',
    'city',
    ...stateFields,
    'zipCode',
    'cellPhone',
    'phone'
  ];

  const priceListFields = [
    'firstName',
    'lastName',
    'email',
    'companyCountry',
    'companyName',
    'companyType',
    'phone',
    ...(form.values.hasMagazineAddon ? ['address', 'city', ...stateFields, 'zipCode'] : [])
  ];

  const componentsList: Array<{ name: string; component: any }> = [
    {
      name: 'firstName',
      component: (
        <Grid item sm={!isPriceList ? 4 : 6} xs={12}>
          <Stack>
            <FieldLabel text="First Name" required>
              <TField
                placeholder="First Name"
                fullWidth
                name="firstName"
                value={form.values.firstName}
                onChange={form.handleChange}
                InputProps={{
                  sx: {
                    fontFamily: 'Inter, Montserrat-Regular, sans-serif',
                    fontSize: '14px'
                  },
                  endAdornment:
                    form.touched.firstName && form.errors.firstName ? (
                      <ErrorIcon>!</ErrorIcon>
                    ) : null
                }}
                error={form.touched.firstName && Boolean(form.errors.firstName)}
                helperText={
                  form.touched.firstName &&
                  form.errors.firstName &&
                  form.errors.firstName !== 'Required'
                    ? form.errors.firstName
                    : null
                }
              />
            </FieldLabel>
          </Stack>
        </Grid>
      )
    },
    {
      name: 'lastName',
      component: (
        <Grid item sm={!isPriceList ? 4 : 6} xs={12}>
          <FieldLabel text="Last Name" required>
            <TField
              placeholder="Last Name"
              fullWidth
              name="lastName"
              value={form.values.lastName}
              onChange={form.handleChange}
              InputProps={{
                sx: {
                  fontFamily: 'Inter, Montserrat-Regular, sans-serif',
                  fontSize: '14px'
                },
                endAdornment:
                  form.touched.lastName && form.errors.lastName ? <ErrorIcon>!</ErrorIcon> : null
              }}
              error={form.touched.lastName && Boolean(form.errors.lastName)}
              helperText={
                form.touched.lastName && form.errors.lastName && form.errors.lastName !== 'Required'
                  ? form.errors.lastName
                  : null
              }
            />
          </FieldLabel>
        </Grid>
      )
    },
    {
      name: 'email',
      component: (
        <Grid item sm={!isPriceList ? 4 : 6} xs={12}>
          <FieldLabel text="Email" required>
            <TField
              placeholder="Email"
              disabled={form.values.fromCache || subscriptionId !== null}
              fullWidth
              name="email"
              value={form.values.email}
              onChange={form.handleChange}
              InputProps={{
                sx: {
                  fontFamily: 'Inter, Montserrat-Regular, sans-serif',
                  fontSize: '14px'
                },
                endAdornment:
                  form.touched.email && form.errors.email ? <ErrorIcon>!</ErrorIcon> : null
              }}
              error={
                form.touched.email &&
                (Boolean(form.errors.email) ||
                  (!isPriceList && errorMessageType === errorListTypes.emailAlreadyExists) ||
                  errorMessageType === errorListTypes.emailIsNotValid)
              }
              helperText={
                <EmailErrorsTextsExists
                  showErrorPopup={showErrorPopup}
                  isPriceList={isPriceList}
                  form={form}
                />
              }
            />
          </FieldLabel>
        </Grid>
      )
    },
    {
      name: 'companyName',
      component: (
        <Grid item xs={12} sm={4}>
          <FieldLabel text="Company Name" required={!isPriceList}>
            <TField
              placeholder="Company Name"
              fullWidth
              name="companyName"
              value={form.values.companyName}
              onChange={form.handleChange}
              InputProps={{
                sx: {
                  fontFamily: 'Inter, Montserrat-Regular, sans-serif',
                  fontSize: '14px'
                },
                endAdornment:
                  form.touched.companyName && form.errors.companyName ? (
                    <ErrorIcon>!</ErrorIcon>
                  ) : null
              }}
              error={form.touched.companyName && Boolean(form.errors.companyName)}
              helperText={
                form.touched.companyName &&
                form.errors.companyName &&
                form.errors.companyName !== 'Required'
                  ? form.errors.companyName
                  : null
              }
            />
          </FieldLabel>
        </Grid>
      )
    },
    {
      name: 'companyType',
      component: (
        <Grid item xs={12} sm={4}>
          <FieldLabel text="Company Type" required={!isPriceList}>
            <SelectWrapper>
              <TSelect
                MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
                placeholder="Company Type"
                size="small"
                name="companyType"
                disabled={disabled}
                value={form.values.companyType}
                onChange={form.handleChange}
                error={form.touched.companyType && Boolean(form.errors.companyType)}
                style={{
                  fontSize: '14px',
                  fontFamily: 'Inter, Montserrat-Regular, sans-serif',
                  width: '100%'
                }}
              >
                {companyTypes
                  .map(type => type.name)
                  .sort()
                  .map((companyType: string) => (
                    <MenuItem key={companyType} value={companyType}>
                      {companyType}
                    </MenuItem>
                  ))}
              </TSelect>
              {form.touched.companyType && Boolean(form.errors.companyType) ? (
                <ErrorIcon>!</ErrorIcon>
              ) : null}
            </SelectWrapper>
          </FieldLabel>
        </Grid>
      )
    },
    {
      name: 'companyCountry',
      component: (
        <Grid item sm={!isPriceList ? 4 : 6} xs={12}>
          <FieldLabel text="Country" required>
            <StyledAutocomplete
              options={countries.sort()}
              value={form.values.companyCountry || null}
              onChange={(event, newValue) => {
                form.setFieldValue('companyCountry', newValue);
              }}
              renderInput={params => (
                <TField
                  {...params}
                  placeholder="Country"
                  size="small"
                  name="companyCountry"
                  error={form.touched.companyCountry && Boolean(form.errors.companyCountry)}
                  helperText={
                    form.touched.companyCountry &&
                    form.errors.companyCountry &&
                    form.errors.companyCountry !== 'Required'
                      ? form.errors.companyCountry
                      : null
                  }
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      fontFamily: 'Inter, Montserrat-Regular, sans-serif',
                      fontSize: '14px'
                    },
                    endAdornment: (
                      <>
                        {form.touched.companyCountry && form.errors.companyCountry ? (
                          <ErrorIcon>!</ErrorIcon>
                        ) : null}
                        {(params.InputProps as any)?.endAdornment}
                      </>
                    )
                  }}
                />
              )}
              disabled={
                form.values.fromCache ||
                subscriptionId !== null ||
                selectedPlan?.packageName.toLowerCase().includes('india')
              }
            />
          </FieldLabel>
        </Grid>
      )
    },
    {
      name: 'address',
      component: (
        <Grid item xs={12} sm={hasStateOptions ? 4 : 7}>
          <FieldLabel text="Address" required>
            <TField
              placeholder="Address"
              fullWidth
              name="address"
              value={form.values.address}
              onChange={form.handleChange}
              InputProps={{
                sx: {
                  fontFamily: 'Inter, Montserrat-Regular, sans-serif',
                  fontSize: '14px'
                },
                endAdornment:
                  form.touched.address && form.errors.address ? <ErrorIcon>!</ErrorIcon> : null
              }}
              error={form.touched.address && Boolean(form.errors.address)}
              helperText={
                form.touched.address && form.errors.address && form.errors.address !== 'Required'
                  ? form.errors.address
                  : null
              }
            />
          </FieldLabel>
        </Grid>
      )
    },
    {
      name: 'city',
      component: (
        <Grid item xs={12} sm={3}>
          <FieldLabel text="City" required>
            <TField
              placeholder="City"
              fullWidth
              name="city"
              value={form.values.city}
              onChange={form.handleChange}
              InputProps={{
                sx: {
                  fontFamily: 'Inter, Montserrat-Regular, sans-serif',
                  fontSize: '14px'
                },
                endAdornment:
                  form.touched.city && form.errors.city ? <ErrorIcon>!</ErrorIcon> : null
              }}
              error={form.touched.city && Boolean(form.errors.city)}
              helperText={
                form.touched.city && form.errors.city && form.errors.city !== 'Required'
                  ? form.errors.city
                  : null
              }
            />
          </FieldLabel>
        </Grid>
      )
    },
    {
      name: 'state',
      component: (
        <Grid item xs={12} sm={3}>
          <FieldLabel
            text="State"
            required={
              form.values.companyCountry?.toLowerCase() === 'usa' ||
              form.values.companyCountry?.toLowerCase() === 'united states'
            }
            error={form.touched.accountState && Boolean(form.errors.accountState)}
          >
            <SelectWrapper>
              <TSelect
                MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
                placeholder="State"
                size="small"
                name="accountState"
                value={form.values.accountState}
                onChange={form.handleChange}
                error={form.touched.accountState && Boolean(form.errors.accountState)}
                style={{
                  fontSize: '14px',
                  fontFamily: 'Inter, Montserrat-Regular, sans-serif',
                  width: '100%'
                }}
              >
                {getStateArrayByCountry(form.values.companyCountry ?? '')
                  .sort()
                  .map((state: string) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
              </TSelect>
              {form.touched.accountState && Boolean(form.errors.accountState) ? (
                <ErrorIcon>!</ErrorIcon>
              ) : null}
            </SelectWrapper>
          </FieldLabel>
        </Grid>
      )
    },
    {
      name: 'zipCode',
      component: (
        <Grid item xs={12} sm={2}>
          <FieldLabel text="Zip Code" required>
            <TField
              placeholder="Zip Code"
              fullWidth
              name="zipCode"
              value={form.values.zipCode}
              onChange={form.handleChange}
              InputProps={{
                sx: {
                  fontFamily: 'Inter, Montserrat-Regular, sans-serif',
                  fontSize: '14px'
                },
                endAdornment:
                  form.touched.zipCode && form.errors.zipCode ? <ErrorIcon>!</ErrorIcon> : null
              }}
              error={form.touched.zipCode && Boolean(form.errors.zipCode)}
              helperText={
                form.touched.zipCode && form.errors.zipCode && form.errors.zipCode !== 'Required'
                  ? form.errors.zipCode
                  : null
              }
            />
          </FieldLabel>
        </Grid>
      )
    },
    {
      name: 'phone',
      component: (
        <Grid id="grid-office-number" item sm={!isPriceList ? 6 : 4} xs={12}>
          <FieldLabel text={!isPriceList ? 'Office Number' : 'Phone Number'}>
            <TField
              id="office-number"
              placeholder="Office Number"
              type="tel"
              fullWidth
              name="phone"
              value={getPhoneValue()}
              onChange={(e: any) => changeOfficePhone(e.target.value)}
              InputProps={{
                endAdornment:
                  form.touched.phone && form.errors.phone ? <ErrorIcon>!</ErrorIcon> : null
              }}
              sx={{ fontFamily: 'Inter, Montserrat-Regular, sans-serif', fontSize: '14px' }}
              error={form.touched.phone && Boolean(form.errors.phone)}
              helperText={
                form.touched.phone && form.errors.phone && form.errors.phone !== 'Required'
                  ? form.errors.phone
                  : null
              }
            />
          </FieldLabel>
        </Grid>
      )
    },
    {
      name: 'cellPhone',
      component: (
        <Grid item xs={12} sm={6}>
          <FieldLabel text="Mobile Number" required={!isPriceList}>
            <TField
              id="mobile-number"
              placeholder="Mobile Number"
              type="tel"
              fullWidth
              name="cellPhone"
              value={getPhoneValue(true)}
              onChange={(e: any) => changeMobilePhone(e.target.value)}
              onBlur={() => form.setFieldTouched('cellPhone', true)}
              InputProps={{
                sx: {
                  fontFamily: 'Inter, Montserrat-Regular, sans-serif',
                  fontSize: '14px'
                },
                endAdornment:
                  form.touched.cellPhone && form.errors.cellPhone ? <ErrorIcon>!</ErrorIcon> : null
              }}
              error={form.touched.cellPhone && Boolean(form.errors.cellPhone)}
              helperText={
                form.touched.cellPhone &&
                form.errors.cellPhone &&
                form.errors.cellPhone !== 'Required'
                  ? form.errors.cellPhone
                  : null
              }
            />
          </FieldLabel>
        </Grid>
      )
    }
  ];

  return (
    <MainContainer gap={2} id="details-section-id">
      <Fieldset disabled={disabled}>
        <Grid container spacing={{ xs: 1, sm: 2 }} rowSpacing={{ xs: 1, sm: 2 }}>
          {isPriceList &&
            priceListFields.map(field => (
              <Fragment key={field}>
                {componentsList.find(c => c.name === field)?.component}
              </Fragment>
            ))}
          {!isPriceList &&
            rapnetFields.map(field => (
              <Fragment key={field}>
                {componentsList.find(c => c.name === field)?.component}
              </Fragment>
            ))}
        </Grid>
      </Fieldset>
    </MainContainer>
  );
};
export default Details;
